'use client'

// React Imports
import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'


// MUI Imports
import { useTheme } from '@mui/material/styles'

// Third-party Imports
import PerfectScrollbar from 'react-perfect-scrollbar'

// Type Imports

// Icon Imports
import DashboardRounded from '@mui/icons-material/DashboardRounded'
import ApartmentRounded from '@mui/icons-material/ApartmentRounded'
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded'
import PersonRounded from '@mui/icons-material/PersonRounded'
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded'
import LocalAtmRounded from '@mui/icons-material/LocalAtmRounded'
import PhoneRounded from '@mui/icons-material/PhoneRounded'
import HandshakeRounded from '@mui/icons-material/HandshakeRounded'
import VolunteerActivismRounded from '@mui/icons-material/VolunteerActivismRounded'
import WorkRounded from '@mui/icons-material/WorkRounded'
import LockRounded from '@mui/icons-material/LockRounded'
import AdminPanelSettingsRounded from '@mui/icons-material/AdminPanelSettingsRounded'
import ViewModuleRounded from '@mui/icons-material/ViewModuleRounded'
import SettingsRounded from '@mui/icons-material/SettingsRounded'
import AccountCircleRounded from '@mui/icons-material/AccountCircleRounded'
import WaterDropRounded from '@mui/icons-material/WaterDropRounded'
import ComputerRounded from '@mui/icons-material/ComputerRounded'
import DirectionsCarRounded from '@mui/icons-material/DirectionsCarRounded'
import EventBusyRounded from '@mui/icons-material/EventBusyRounded'
import EventNoteRounded from '@mui/icons-material/EventNoteRounded'

import type { VerticalMenuDataType } from '@/types/menuTypes'
import type { MenuItem as GiaMenuItem, MenuResponse, MenuSection as GiaMenuSection } from '@/types/gia'
import type { VerticalMenuContextProps } from '@menu/components/vertical-menu/Menu'
import type { getDictionary } from '@/utils/getDictionary'

// Component Imports
import { Menu } from '@menu/vertical-menu'
import { GenerateVerticalMenu } from '@components/GenerateMenu'

// Hook Imports
import useVerticalNav from '@menu/hooks/useVerticalNav'
import { useAuth } from '@/contexts/auth-context'

// Styled Component Imports
import StyledVerticalNavExpandIcon from '@menu/styles/vertical/StyledVerticalNavExpandIcon'

// Style Imports
import menuItemStyles from '@core/styles/vertical/menuItemStyles'
import menuSectionStyles from '@core/styles/vertical/menuSectionStyles'

// API Imports
import { menuApi } from '@/lib/api'

type RenderExpandIconProps = {
  open?: boolean
  transitionDuration?: VerticalMenuContextProps['transitionDuration']
}

type Props = {
  dictionary: Awaited<ReturnType<typeof getDictionary>>
  scrollMenu: (container: any, isPerfectScrollbar: boolean) => void
}

const defaultIconByCode: Record<string, ReactNode> = {
  dashboard: <DashboardRounded fontSize='small' />,
  centros: <ApartmentRounded fontSize='small' />,
  personas: <PeopleAltRounded fontSize='small' />,
  personas_todos: <PeopleAltRounded fontSize='small' />,
  usuarios: <PersonRounded fontSize='small' />,
  dietas: <ReceiptLongRounded fontSize='small' />,
  dietas_types: <LocalAtmRounded fontSize='small' />,
  ausencias: <EventBusyRounded fontSize='small' />,
  ausencias_types: <EventNoteRounded fontSize='small' />,
  llamadas: <PhoneRounded fontSize='small' />,
  call_logs: <PhoneRounded fontSize='small' />,
  diuresis: <WaterDropRounded fontSize='small' />,
  pdis: <HandshakeRounded fontSize='small' />,
  atendidos: <HandshakeRounded fontSize='small' />,
  voluntarios: <VolunteerActivismRounded fontSize='small' />,
  trabajadores: <WorkRounded fontSize='small' />,
  permisos: <LockRounded fontSize='small' />,
  roles: <AdminPanelSettingsRounded fontSize='small' />,
  modulos: <ViewModuleRounded fontSize='small' />,
  centro_modules: <ViewModuleRounded fontSize='small' />,
  settings: <SettingsRounded fontSize='small' />,
  administracion: <SettingsRounded fontSize='small' />,
  perfil: <AccountCircleRounded fontSize='small' />,
  it: <ComputerRounded fontSize='small' />,
  transporte: <DirectionsCarRounded fontSize='small' />
}

const normalizeKey = (value?: string) =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]/g, '')

const resolveIcon = (item: GiaMenuItem) => {
  const rawIcon = (item.icon || '').trim()
  const codeKey = normalizeKey(item.code)
  const urlKey = normalizeKey(item.url)
  const nameKey = normalizeKey(item.name)

  return (
    defaultIconByCode[item.code] ||
    defaultIconByCode[item.url?.replace('/', '')] ||
    defaultIconByCode[codeKey] ||
    defaultIconByCode[urlKey] ||
    defaultIconByCode[nameKey] ||
    (rawIcon || 'tabler-circle')
  )
}

const RenderExpandIcon = ({ open, transitionDuration }: RenderExpandIconProps) => (
  <StyledVerticalNavExpandIcon open={open} transitionDuration={transitionDuration}>
    <i className='tabler-chevron-right' />
  </StyledVerticalNavExpandIcon>
)

const VerticalMenu = ({ dictionary, scrollMenu }: Props) => {
  // Hooks
  const theme = useTheme()
  const verticalNavOptions = useVerticalNav()
  const { isAuthenticated, impersonatedCentroId } = useAuth()

  // State
  const [menuData, setMenuData] = useState<VerticalMenuDataType[]>([])

  // Vars
  const { isBreakpointReached, transitionDuration } = verticalNavOptions

  const ScrollWrapper = isBreakpointReached ? 'div' : PerfectScrollbar

  const toVerticalMenuItem = (item: GiaMenuItem): VerticalMenuDataType => {
    const hasChildren = !!item.children?.length
    const icon = resolveIcon(item) || undefined
    const children = item.children ? [...item.children] : []

    if (item.code === 'personas') {
      const hasTodos = children.some(child => child.code === 'personas_todos' || child.url === '/personas')

      if (!hasTodos) {
        children.unshift({
          code: 'personas_todos',
          name: 'Todos',
          icon: '',
          url: '/personas',
          children: []
        })
      }
    }

    if (hasChildren || children.length > 0) {
      return {
        label: item.name,
        icon,
        children: children.map(toVerticalMenuItem)
      }
    }

    return {
      label: item.name,
      icon,
      href: item.url,
      exactMatch: true
    }
  }

  const buildMenuData = (response: MenuResponse): VerticalMenuDataType[] => {
    const sections = response?.sections ?? []

    return sections.flatMap((section: GiaMenuSection) => {
      if (!section.items?.length) {
        return []
      }

      return [
        {
          isSection: true,
          label: section.name,
          children: section.items.map(toVerticalMenuItem)
        }
      ]
    })
  }

  const menuFallback = useMemo<VerticalMenuDataType[]>(
    () => [
      {
        isSection: true,
        label: dictionary['navigation']?.dashboards ?? 'Menú',
        children: [
          {
            label: dictionary['navigation']?.analytics ?? 'Dashboard',
            href: '/dashboard',
            icon: 'tabler-smart-home',
            exactMatch: true
          }
        ]
      }
    ],
    [dictionary]
  )

  useEffect(() => {
    let active = true

    const loadMenu = async () => {
      if (!isAuthenticated) {
        setMenuData(menuFallback)

        return
      }

      try {
        const response = await menuApi.getMenu(impersonatedCentroId)

        console.log('[DEBUG] Menu API response:', JSON.stringify(response, null, 2))

        if (!active) return

        const items = buildMenuData(response)

        setMenuData(items.length ? items : menuFallback)
      } catch (err) {
        console.error('[DEBUG] Menu API error:', err)
        if (!active) return

        setMenuData(menuFallback)
      }
    }

    loadMenu()

    const handleMenuUpdated = () => {
      loadMenu()
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('gia:menu-updated', handleMenuUpdated)
    }

    return () => {
      active = false

      if (typeof window !== 'undefined') {
        window.removeEventListener('gia:menu-updated', handleMenuUpdated)
      }
    }
  }, [isAuthenticated, menuFallback, impersonatedCentroId])

  return (
    // eslint-disable-next-line lines-around-comment
    /* Custom scrollbar instead of browser scroll, remove if you want browser scroll only */
    <ScrollWrapper
      {...(isBreakpointReached
        ? {
            className: 'bs-full overflow-y-auto overflow-x-hidden',
            onScroll: container => scrollMenu(container, false)
          }
        : {
            options: { wheelPropagation: false, suppressScrollX: true },
            onScrollY: container => scrollMenu(container, true)
          })}
    >
      <Menu
        popoutMenuOffset={{ mainAxis: 23 }}
        menuItemStyles={menuItemStyles(verticalNavOptions, theme)}
        renderExpandIcon={({ open }) => <RenderExpandIcon open={open} transitionDuration={transitionDuration} />}
        renderExpandedMenuItemIcon={{ icon: <i className='tabler-circle text-xs' /> }}
        menuSectionStyles={menuSectionStyles(verticalNavOptions, theme)}
      >
        <GenerateVerticalMenu menuData={menuData.length ? menuData : menuFallback} />
      </Menu>
    </ScrollWrapper>
  )
}

export default VerticalMenu
